import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { CalculatorSlider } from '../CalculatorSlider';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
function formatEuro(value: number) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);
}
export function SlideCampaignCostCalculator({}: SlideProps) {
  const [brands, setBrands] = useState(1);
  const [campaignsPerYear, setCampaignsPerYear] = useState(4);
  const [countries, setCountries] = useState(1);
  const [economicsOpen, setEconomicsOpen] = useState(false);
  const [annualCostVisible, setAnnualCostVisible] = useState(false);
  const calculation = useMemo(() => {
    const masterBase = 2900;
    const masterFloor = 1900;
    const followUpMaster = 1000;
    const localizationBase = 999;
    const monthlyInsightsBase = 99;
    const campaignDuration = 3;
    const annualCampaigns = brands * campaignsPerYear;
    const masterProgress = Math.min(1, Math.max(0, (annualCampaigns - 1) / 99));
    const masterCost = masterBase - (masterBase - masterFloor) * masterProgress;
    const countryDiscount = Math.min(0.5, Math.max(0, (countries - 2) / 48 * 0.5));
    const localizationCost = localizationBase * (1 - countryDiscount);
    const insightsDiscount = Math.min(0.5, Math.max(0, (countries - 2) / 48 * 0.5));
    const monthlyInsights = monthlyInsightsBase * (1 - insightsDiscount);
    const perCountryActivation = localizationCost + monthlyInsights * campaignDuration;
    const firstCampaign = masterCost + perCountryActivation * countries;
    const followUpCampaign = followUpMaster + perCountryActivation * countries;
    const totalAnnualCost = brands * firstCampaign + Math.max(0, annualCampaigns - brands) * followUpCampaign;
    const totalDeployments = annualCampaigns * countries;
    return {
      masterCost,
      localizationCost,
      monthlyInsights,
      annualCampaigns,
      totalDeployments,
      totalAnnualCost,
      costPerDeployment: totalDeployments ? totalAnnualCost / totalDeployments : 0
    };
  }, [brands, campaignsPerYear, countries]);
  return <InteriorSlide surface data-id="element-144" data-mp="xuc8gjtrxze0">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.8vw,1.6rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-145" data-mp="xukmwg5jpcrb">
        <div data-id="element-146" data-mp="1btk9li9hzhku">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-147" data-mp="1bt25y0kia12w">
            Portfolio economics
          </p>
          <h2 className="max-w-4xl text-[clamp(2rem,3.4vw,3.25rem)] font-extrabold leading-[1.03] tracking-[-0.04em] text-[var(--fg)]" data-id="element-148" data-mp="1gqkevqyiwgaw">
            See for yourself—move the sliders to see{' '}
            <span className="text-[var(--brand)]" data-id="element-149" data-mp="3im4al948x07q">your potential.</span>
          </h2>
        </div>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-150" data-mp="369sik2si3ch9">
        <motion.section initial={{
        opacity: 0,
        x: -18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.15,
        duration: 0.55
      }} className="flex min-h-0 flex-col gap-4 lg:col-span-5" aria-label="Portfolio calculator inputs" data-id="element-151" data-mp="367piepxmw2yt">
          <article className="rounded-3xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-152" data-mp="3txaca3vbskmv">
            <div className="mb-5 flex items-center gap-3" data-id="element-153" data-mp="3oy762g3m08db">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-154" data-mp="3k05pq2xkin5j">
                <SlidersHorizontalIcon className="h-5 w-5" aria-hidden="true" data-id="element-155" data-mp="3fzc1t9k4l8cn" />
              </span>
              <div data-id="element-156" data-mp="28nd13102qetp">
                <h3 className="font-extrabold text-[var(--fg)]" data-id="element-157" data-mp="28jp6rzmt1odx">Portfolio scale</h3>
                <p className="text-xs text-[var(--muted)]" data-id="element-158" data-mp="24mjafzv8ylx4">Revenue updates instantly.</p>
              </div>
            </div>
            <div className="space-y-5" data-id="element-159" data-mp="12gujmedzogh">
              <CalculatorSlider label="Number of Countries" value={countries} min={1} max={130} onChange={setCountries} data-id="element-160" data-mp="138o6rofbc44" />
              <CalculatorSlider label="Frequency of campaigns annually" value={campaignsPerYear} min={1} max={12} onChange={setCampaignsPerYear} data-id="element-161" data-mp="3ikrw4ccmzmiy" />
              <CalculatorSlider label="Number of Brands" value={brands} min={1} max={40} onChange={setBrands} data-id="element-162" data-mp="f1q80eb7k4gr" />
            </div>
          </article>

          <article className="overflow-hidden rounded-2xl border border-[var(--brand)]/10 bg-white" data-id="element-163" data-mp="k0jn2b6583j1">
            <button type="button" onClick={() => setEconomicsOpen(open => !open)} aria-expanded={economicsOpen} className="flex w-full items-center gap-3 px-5 py-3 text-left transition hover:bg-[var(--brand)]/[0.03]" data-id="element-164" data-mp="61a81etobfgn">
              <div className="h-5 w-5 text-[var(--brand)]" aria-hidden="true" data-id="element-165" data-mp="2qu58gx3srl0b" />
              <span className="flex-1" data-id="element-166" data-mp="2uup8c8fr92q5">
                <span className="block text-sm font-bold text-[var(--fg)]" data-id="element-167" data-mp="2n9lbat4lextb">Unit economics</span>
                <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--muted)]" data-id="element-168" data-mp="2ib0jni3epmgh">Discounted for scale</span>
              </span>
              <ChevronDownIcon className={`h-4 w-4 text-[var(--muted)] transition ${economicsOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-169" data-mp="2x5ydygi48m07" />
            </button>
            <AnimatePresence initial={false} data-id="element-170" data-mp="24key1lmkiyew">
              {economicsOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-171" data-mp="1zkiutolp3b0e">
                  <div className="grid grid-cols-3 gap-2 border-t border-[var(--brand)]/10 bg-[var(--brand)]/[0.03] p-3" data-id="element-172" data-mp="1lsvy5nnuc0h3">
                    <Rate label="Master Snak" value={formatEuro(calculation.masterCost)} data-id="element-173" data-mp="1guaybpci3it6" />
                    <Rate label="Localization" value={formatEuro(calculation.localizationCost)} data-id="element-174" data-mp="26i6iejqtrztv" />
                    <Rate label="Insights / month" value={formatEuro(calculation.monthlyInsights)} data-id="element-175" data-mp="2aja25hbzug0d" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </article>
        </motion.section>

        <motion.section initial={{
        opacity: 0,
        x: 18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.27,
        duration: 0.55
      }} className="flex min-h-0 flex-col gap-4 lg:col-span-7" aria-label="Portfolio calculator results" data-id="element-176" data-mp="2k8a0qzml39e1">
          <article className="relative overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.5rem,2.5vw,2.5rem)] text-white shadow-lg" data-id="element-177" data-mp="1wjhanjsn7vrc">
            <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full border-[34px] border-white/10" data-id="element-178" data-mp="21k4zbyteg0ie" />
            <p className="relative text-xs font-bold uppercase tracking-[0.16em] text-white/65" data-id="element-179" data-mp="381a2avbmdxth">
              Cost per localized campaign
            </p>
            <motion.p key={calculation.costPerDeployment} initial={{
            opacity: 0.6,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} className="relative mt-3 text-[clamp(3.25rem,7vw,6.5rem)] font-extrabold leading-none tracking-[-0.06em]" data-id="element-180" data-mp="119lvy71ioqvz">
              {formatEuro(calculation.costPerDeployment)}
            </motion.p>
            <p className="relative mt-3 text-sm text-white/70" data-id="element-181" data-mp="ikp1a227jwd8">Per localized deployment</p>
          </article>

          <div className="grid grid-cols-2 gap-4" data-id="element-182" data-mp="dkt52ozr5988">
            <ResultCard value={calculation.annualCampaigns.toLocaleString()} label="Master campaigns / year" data-id="element-183" data-mp="dnfeku67dyt3" />
            <ResultCard value={calculation.totalDeployments.toLocaleString()} label="Localized deployments / year" accent data-id="element-184" data-mp="3mwmxd8ede0m" />
          </div>

          <article className="flex flex-1 items-center justify-between gap-6 rounded-3xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-185" data-mp="22yxzj9ip02s8">
            <div data-id="element-186" data-mp="1xz39f86qrh88">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--muted)]" data-id="element-187" data-mp="16vtv3syeifi0">
                Estimated annual cost
              </p>
              <p className="mt-2 text-sm leading-relaxed text-[var(--muted)]" data-id="element-188" data-mp="1bu527vxbabwj">
                Start with a Netherlands pilot, prove the concept, then scale across North Europe and the wider Sanofi portfolio—we’re working toward a pricing model based on actual impact.
              </p>
            </div>
            <div className="flex-shrink-0 text-right" data-id="element-189" data-mp="o3r9e36wk90w">
              <AnimatePresence mode="wait" data-id="element-190" data-mp="t4fc3m4hueay">
                {annualCostVisible ? <motion.div key="cost" initial={{
                opacity: 0,
                y: 5
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -5
              }} className="flex items-center justify-end gap-2" data-id="element-191" data-mp="t1svkwzmkogl">
                    <p className="text-3xl font-extrabold text-[var(--brand)]" data-id="element-192" data-mp="2djqt2e85dgus">
                      {formatEuro(calculation.totalAnnualCost)}
                    </p>
                    <button type="button" onClick={() => setAnnualCostVisible(false)} aria-label="Close annual cost" className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--brand)]/10 text-[var(--brand)] transition hover:bg-[var(--brand)] hover:text-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--brand)]/30" data-id="element-193" data-mp="1uklklq9qyzu">
                      <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-194" data-mp="gl1jvw7lx8si" />
                    </button>
                  </motion.div> : <motion.p key="hidden" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="text-lg font-bold text-[var(--muted)]" data-id="element-195" data-mp="ukjdcovula87">
                    Reveal total
                  </motion.p>}
              </AnimatePresence>
              {!annualCostVisible && <button type="button" onClick={() => setAnnualCostVisible(true)} aria-pressed={annualCostVisible} className="mt-2 rounded-full bg-[var(--brand)]/10 px-4 py-2 text-xs font-bold text-[var(--brand)] transition hover:bg-[var(--brand)]/15" data-id="element-196" data-mp="2rfyzz22zs1ou">
                  Show annual cost
                </button>}
            </div>
          </article>
        </motion.section>
      </div>
    </InteriorSlide>;
}
function Rate({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="rounded-xl bg-white p-3 text-center" data-id="element-197" data-mp="2zrx7thlvtl4q">
      <p className="text-lg font-extrabold text-[var(--brand)]" data-id="element-198" data-mp="2vqv68u4hswzl">{value}</p>
      <p className="mt-1 text-[9px] font-bold uppercase tracking-wide text-[var(--muted)]" data-id="element-199" data-mp="2r0503eo8sq3u">{label}</p>
    </div>;
}
function ResultCard({
  value,
  label,
  accent = false
}: {
  value: string;
  label: string;
  accent?: boolean;
}) {
  return <article className="rounded-2xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-200" data-mp="3jhhaeabdlx4b">
      <p className={`text-3xl font-extrabold ${accent ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`} data-id="element-201" data-mp="3ep7dgabufwsj">{value}</p>
      <p className="mt-2 text-xs font-semibold text-[var(--muted)]" data-id="element-202" data-mp="3m2os4u8e4o6a">{label}</p>
    </article>;
}